// @flow

import { logger, setItem, getQuery, getQueryFallback, LOGGING_COOKIE_NAME } from '../common'
import { defer } from '../common/defer'

import { createClickout } from '../common/api/chatchampApi'
import {
  ALIGN_BOT_DESKTOP,
  ALIGN_BOT_MOBILE,
  IFRAME_CUSTOMER_ID,
  IFRAME_IS_MOBILE,
  IFRAME_PARENT_URL,
  IS_ENABLED_AB_TEST,
  IS_ENABLED_POSTHOG
} from '../common/iframeParameters'
import { parseJSONOrEmptyObject } from '../common/utils'
import { registerChatbotEventListeners } from '../main/eventTracking'
import { isHiddenByFeatureFlag, loadFeatureFlags, initPosthog } from '../main/eventTracking/posthog'

import { createApp } from './app'
import { createStore } from './store'

import {
  CUSTOM_WEBSITE_CHAT_CONFIG,
  getSubscriberIdFromQuery,
  LAST_MESSAGE_RECEIVED_COOKIE_NAME,
  WELCOME_MESSAGE_ACTIVELY_CLOSED_COOKIE_NAME,
  WELCOME_MESSAGE_PAYLOAD
} from './models'

import { initMessageBus } from './messageBus'

export function startup (): void {
  const customerId = getQueryFallback(IFRAME_CUSTOMER_ID, null)
  if (!customerId) {
    // eslint-disable-next-line no-console
    console.warn('Chatchamp was not initialized properly.')
    return
  }

  let parentUrl = ''
  let isMobile = true
  let isEnabledPostHog = false
  let alignBotMobile = 'right'
  let alignBotDesktop = 'right'
  try {
    parentUrl = getQuery(IFRAME_PARENT_URL)
    isMobile = (getQuery(IFRAME_IS_MOBILE) === 'true')
    isEnabledPostHog = (getQuery(IS_ENABLED_POSTHOG) === 'true')
    alignBotMobile = getQuery(ALIGN_BOT_MOBILE)
    alignBotDesktop = getQuery(ALIGN_BOT_DESKTOP)
  } catch (err) {
    logger.error(err)
  }

  // initialize store
  const subscriberId = getSubscriberIdFromQuery()
  const store = createStore(customerId, subscriberId, parentUrl, isMobile ? 'mobile' : 'desktop')
  store.commit('setLastMessageReceived', getQueryFallback(LAST_MESSAGE_RECEIVED_COOKIE_NAME, null))
  const welcomeMessagePayload = getQueryFallback(WELCOME_MESSAGE_PAYLOAD, null)
  if (!welcomeMessagePayload) store.commit('setClosedByUserAt', getQueryFallback(WELCOME_MESSAGE_ACTIVELY_CLOSED_COOKIE_NAME, null))
  if (welcomeMessagePayload) {
    store.commit('setForceShowWelcomeMessage', {
      value: true,
      payload: welcomeMessagePayload
    })
  }
  const customWebsiteChatConfig = getQueryFallback(CUSTOM_WEBSITE_CHAT_CONFIG, null)
  if (customWebsiteChatConfig) store.commit('setCustomWebsiteChatConfig', parseJSONOrEmptyObject(customWebsiteChatConfig))

  window.websocketReadyPromise = defer()
  window.bubbleStore = store

  initMessageBus(store)

  if (process.env.E2E_TEST) {
    // enable mocking of requests in iframe for cypress
    try {
      window.XMLHttpRequest = window.parent.window.XMLHttpRequest
    } catch (e) {
      // ignore error
    }
  }

  window.addEventListener('DOMContentLoaded', () => {
    if (isMobile && document.body) document.body.classList.add('is-mobile')
  })

  window.vueInstance = null

  async function initChatbot () {
    if (document.visibilityState === 'visible' && !window.vueInstance) {
      let posthogInstance = null
      if (isEnabledPostHog) {
        posthogInstance = initPosthog(subscriberId)
        if (posthogInstance && getQueryFallback(IS_ENABLED_AB_TEST, 'false') === 'true') {
          await loadFeatureFlags(customerId, subscriberId)
          if (await isHiddenByFeatureFlag(customerId, subscriberId)) {
            logger.info('Chatchamp website chat was hidden by Posthog.')
            return
          }
        }
      }
      registerChatbotEventListeners()
      window.vueInstance = createApp(store, posthogInstance, {
        alignBotDesktop,
        alignBotMobile
      })
    }
  }

  window.addEventListener('visibilitychange', initChatbot)
  window.addEventListener('load', initChatbot)

  window.chatchamp = {
    products: [],
    setDebugMode: () => {
      setItem(LOGGING_COOKIE_NAME, true)
      logger.info('Activated debug mode.')
    },
    trackLink: (url: string, messageId: number) => {
      createClickout(customerId, subscriberId, messageId, { url })
        .catch((err) => logger.error(err))
    }
  }
}
